import type { MenuContext, MenuScreen } from '../types.js';
import { createScreenShell, createMenuHeader, createCrestButton, el } from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';

interface RuleItem {
  readonly title: string;
  readonly body: string;
}

interface VariantBlock {
  readonly title: string;
  readonly items: readonly RuleItem[];
}

function goatBlock(): VariantBlock {
  return {
    title: t('rules.goat.title'),
    items: [
      { title: t('rules.goat.items.setup.title'), body: t('rules.goat.items.setup.body') },
      { title: t('rules.goat.items.lead.title'), body: t('rules.goat.items.lead.body') },
      { title: t('rules.goat.items.play.title'), body: t('rules.goat.items.play.body') },
      { title: t('rules.goat.items.ending.title'), body: t('rules.goat.items.ending.body') },
      { title: t('rules.goat.items.scoring.title'), body: t('rules.goat.items.scoring.body') },
      { title: t('rules.goat.items.match.title'), body: t('rules.goat.items.match.body') },
      { title: t('rules.goat.items.options.title'), body: t('rules.goat.items.options.body') },
    ],
  };
}

function allFivesBlock(): VariantBlock {
  return {
    title: t('rules.all-fives.title'),
    items: [
      { title: t('rules.all-fives.items.setup.title'), body: t('rules.all-fives.items.setup.body') },
      { title: t('rules.all-fives.items.play.title'), body: t('rules.all-fives.items.play.body') },
      { title: t('rules.all-fives.items.scoring.title'), body: t('rules.all-fives.items.scoring.body') },
      { title: t('rules.all-fives.items.muggins.title'), body: t('rules.all-fives.items.muggins.body') },
      { title: t('rules.all-fives.items.ending.title'), body: t('rules.all-fives.items.ending.body') },
    ],
  };
}

function telefonBlock(): VariantBlock {
  return {
    title: t('rules.telefon.title'),
    items: [
      { title: t('rules.telefon.items.setup.title'), body: t('rules.telefon.items.setup.body') },
      { title: t('rules.telefon.items.rotation.title'), body: t('rules.telefon.items.rotation.body') },
      { title: t('rules.telefon.items.scoring.title'), body: t('rules.telefon.items.scoring.body') },
      { title: t('rules.telefon.items.ending.title'), body: t('rules.telefon.items.ending.body') },
    ],
  };
}

function appendVariantBlock(body: HTMLElement, block: VariantBlock): void {
  body.appendChild(el('div', { class: 'rules-variant-title', text: block.title }));
  const list = el('div', {
    class: 'rules',
    attrs: {
      role: 'region',
      'aria-label': `${t('rules.screenTitle')} — ${block.title}`,
      tabindex: '0',
    },
  });
  block.items.forEach((item, idx) => {
    const num = `§ ${String(idx + 1).padStart(2, '0')}`;
    list.appendChild(
      el('article', { class: 'rule-card' }, [
        el('h3', { class: 'rule-card__heading', text: `${num} · ${item.title}` }),
        el('div', { class: 'rule-card__body', text: item.body }),
      ]),
    );
  });
  body.appendChild(list);
}

export function createRulesScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  return {
    mount(root: HTMLElement, ctx: MenuContext) {
      const shell = createScreenShell();
      frame = shell.frame;
      const body = shell.body;

      body.appendChild(createMenuHeader({ title: t('rules.screenTitle') }));
      appendVariantBlock(body, goatBlock());
      appendVariantBlock(body, telefonBlock());
      appendVariantBlock(body, allFivesBlock());

      const back = el('div', { class: 'button-row back-button-row' }, [
        createCrestButton({ label: t('common.back'), small: true, onClick: () => ctx.navigate('menu') }),
      ]);
      body.appendChild(back);

      root.appendChild(frame);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
